import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ActivityStatusTabs, { ACTIVITY_STATUS_TABS } from '../../components/ActivityStatusTabs';
import { HomeNavButton } from '../../components/NavButtons';
import { getStudentChallenges } from '../../services/challengeService';
import { focusTheme, getListCardProps } from '../../utils/focusTheme';

const statusConfig = {
  submitted: {
    ...focusTheme.status.submitted,
    label: 'چالش حل شده',
  },
  published: {
    ...focusTheme.status.published,
    label: 'شروع چالش',
  },
  expired: {
    ...focusTheme.status.expired,
    label: 'مهلت گذشته',
  },
};

function ArrowCircleIcon({ className = 'text-teal-500' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M8.63612 5.41078L6.69336 7.21477C6.38802 7.4983 6.38802 7.98157 6.69336 8.2651L8.63612 10.0691M7.70447 0.537475C3.74645 0.537475 0.537843 3.74608 0.537843 7.7041C0.537843 11.6621 3.74645 14.8707 7.70447 14.8707C11.6625 14.8707 14.8711 11.6621 14.8711 7.7041C14.8711 3.74608 11.6625 0.537475 7.70447 0.537475Z" stroke="currentColor" strokeWidth="1.07499" strokeLinecap="round" />
    </svg>
  );
}

function formatChallengeDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

function getChallengeViewStatus(challenge) {
  if (challenge.submission) {
    return 'submitted';
  }

  if (challenge.dueAt && new Date(challenge.dueAt).getTime() < Date.now()) {
    return 'expired';
  }

  return challenge.status || 'published';
}


function ChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeTab, setActiveTab] = useState(ACTIVITY_STATUS_TABS.remaining);

  const remainingChallenges = useMemo(
    () => challenges.filter((challenge) => getChallengeViewStatus(challenge) !== 'submitted'),
    [challenges]
  );
  const completedChallenges = useMemo(
    () => challenges.filter((challenge) => getChallengeViewStatus(challenge) === 'submitted'),
    [challenges]
  );
  const visibleChallenges = activeTab === ACTIVITY_STATUS_TABS.completed ? completedChallenges : remainingChallenges;
  const remainingCount = remainingChallenges.length;
  const completedCount = completedChallenges.length;
  const nextChallenge = remainingChallenges[0];

  useEffect(() => {
    let isMounted = true;

    async function loadChallenges() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const challengeItems = await getStudentChallenges();

        if (isMounted) {
          setChallenges(challengeItems);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت چالش‌ها وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadChallenges();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className='flex items-center gap-2'>
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className='px-6 py-1 rounded-[10px] text-[15px] text-white'
          >
            چالش‌های من
          </p>

          <img className='h-[50px]' src='/images/challange.webp' alt='' />
        </div>

        <HomeNavButton />
      </div>

      <div>
        <p className='text-[14px] font-semibold text-slate-900'>
          لیست چالش‌ها
        </p>

        <p className='mt-1 text-[13px] font-medium leading-6 text-slate-800'>
          چالش امروزت رو انتخاب کن و سوالاتش رو با دقت جواب بده.
        </p>
      </div>

      <ActivityStatusTabs
        remainingCount={remainingCount}
        completedCount={completedCount}
        remainingLabel="انجام نشده"
        completedLabel="انجام شده"
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <div
        className='flex flex-col gap-2 rounded-[8px] p-2'
        style={{ background: focusTheme.sectionBg }}
      >
        {isLoading ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center text-[12px] text-slate-500">
            در حال دریافت چالش‌ها...
          </div>
        ) : errorMessage ? (
          <div className="rounded-[8px] bg-white px-4 py-5 text-center">
            <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
            >
              تلاش دوباره
            </button>
          </div>
        ) : challenges.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">فعلا چالشی نداری</p>
            <p className="mt-2 text-[11px] text-slate-500">وقتی چالش جدید منتشر شود، همینجا نمایش داده می‌شود.</p>
          </div>
        ) : visibleChallenges.length === 0 ? (
          <div className="rounded-[8px] bg-white px-4 py-6 text-center">
            <p className="text-[13px] font-bold text-slate-800">
              {activeTab === ACTIVITY_STATUS_TABS.completed
                ? 'هنوز چالشی انجام ندادی'
                : 'الان چالش انجام‌نشده‌ای نداری'}
            </p>
            <p className="mt-2 text-[11px] text-slate-500">
              {activeTab === ACTIVITY_STATUS_TABS.completed
                ? 'بعد از ثبت چالش، اینجا نمایش داده می‌شود.'
                : 'همه چالش‌ها انجام شده‌اند یا هنوز مورد جدیدی منتشر نشده است.'}
            </p>
          </div>
        ) : (
          visibleChallenges.map((challenge, index) => {
            const viewStatus = getChallengeViewStatus(challenge);
            const status = statusConfig[viewStatus] || statusConfig.published;
            const isSubmitted = viewStatus === 'submitted';
            const cardProps = getListCardProps(viewStatus);

            return (
              <Link
                key={challenge.id}
                to={`/challenges/${challenge.id}`}
                className={cardProps.className}
                style={cardProps.style}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {isSubmitted ? (
                      <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] ${focusTheme.submitted.badge}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      </span>
                    ) : (
                      <span className={`flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[5px] text-[12px] font-bold ${status.badgeClass}`}>
                        {index + 1}
                      </span>
                    )}

                    <p className="min-w-0 truncate text-[14px] font-semibold text-slate-900">{challenge.title || 'بدون عنوان'}</p>
                  </div>

                  <ArrowCircleIcon className={status.iconClass} />
                </div>

                {!isSubmitted && challenge.description?.trim() && (
                  <p className="line-clamp-2 text-[13px] leading-6 text-slate-700">
                    {challenge.description}
                  </p>
                )}

                <div className={`grid ${isSubmitted ? 'grid-cols-2' : 'grid-cols-3'} gap-2 text-[11px]`}>
                  <div className={focusTheme.meta.chip}>
                    مهلت: {formatChallengeDate(challenge.dueAt)}
                  </div>

                  {!isSubmitted && (
                    <div className={`${focusTheme.meta.chip} text-center`}>
                      {challenge.questionCount || 0} سوال
                    </div>
                  )}

                  <div className={`${focusTheme.meta.status} ${status.labelClass}`}>
                    {status.label}
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {activeTab === ACTIVITY_STATUS_TABS.remaining && (
        <div className='w-full mx-auto flex flex-col gap-2 items-center justify-center'>
          <p className='text-[13px] font-medium text-slate-800'>
            هر چالش رو باز کن، سوالاتش رو حل کن و بعد برو سراغ بعدی :)
          </p>

          {nextChallenge && (
            <Link
              to={`/challenges/${nextChallenge.id}`}
              className={`w-11/12 mx-auto text-[14px] font-bold py-2.5 text-center flex items-center justify-center rounded-[8px] ${focusTheme.button.primary}`}
            >
              شروع اولین چالش
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default ChallengesPage;
